import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

const AuthCtx = createContext(null);

// LocalStorage sentinel — set on successful login/register, cleared on logout.
// Lets us skip the noisy /auth/me 401 on cold start when the user has never
// signed in on this device (or has explicitly logged out).
const SESSION_FLAG = "tb_had_session";
const hadSession = () => {
  try { return typeof window !== "undefined" && localStorage.getItem(SESSION_FLAG) === "1"; }
  catch { return false; }
};
const setHadSession = (v) => {
  try {
    if (v) localStorage.setItem(SESSION_FLAG, "1");
    else localStorage.removeItem(SESSION_FLAG);
  } catch { /* ignore */ }
};

export function AuthProvider({ children }) {
  // null = unknown / loading; false = unauthenticated; object = user
  const [user, setUser] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      setHadSession(true);
    } catch {
      setUser(false);
      setHadSession(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      return;
    }
    // Skip the initial /auth/me probe (and its 401) when we've never seen a
    // session on this device. Go straight to `false` — user must sign in.
    if (!hadSession()) {
      setUser(false);
      return;
    }
    refresh();
  }, [refresh]);

  const login = async (email, password, remember = true) => {
    const { data } = await api.post("/auth/login", { email, password, remember });
    setUser(data);
    setHadSession(true);
    return data;
  };

  const register = async (email, password, name) => {
    const referral_code = localStorage.getItem("tb_ref_code") || undefined;
    const { data } = await api.post("/auth/register", { email, password, name, referral_code });
    if (referral_code) localStorage.removeItem("tb_ref_code");
    setUser(data);
    setHadSession(true);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Logout should still clear local state even if the network call fails.
      console.warn("[AuthContext] logout request failed:", err);
    }
    setUser(false);
    setHadSession(false);
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
    // THIS BREAKS THE AUTH.
    //
    // Persist the post-auth destination in sessionStorage BEFORE we
    // leave the SPA — Emergent OAuth strips search params from the
    // redirect URL, so `?next=` on `/auth` won't survive the round-
    // trip. `<Protected/>` also writes the same key on auth-wall
    // hits, so this branch runs even when the user starts the Google
    // flow directly from `/auth?next=…` (e.g. a friend's share link).
    try {
      const params = new URLSearchParams(window.location.search);
      const nextFromQs = params.get("next");
      if (nextFromQs) {
        window.sessionStorage.setItem("tb_post_auth_next", nextFromQs);
      }
    } catch { /* private-mode / storage disabled — noop */ }
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href =
      `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AuthCtx.Provider value={{
      user, login, register, logout, refresh, loginWithGoogle, formatApiErrorDetail,
    }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}
